import { OktaAuth, AccessToken } from '@okta/okta-auth-js';
import { AuthProvider } from 'react-admin';
import { getScopes, REDIRECT_URI } from './config';
import { History } from 'history'

export const useAuthProvider = (oktaAuth: OktaAuth, history: History): AuthProvider => {

  const clearSessionStorage = () => {
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('permissions')
    sessionStorage.removeItem('user')
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    oktaAuth.tokenManager.clear()
  }

  return {
    login: () => {return Promise.resolve()},
    checkError: (error: any) => { 
      const status = error.status
      if (status === 401 || status === 403) {
        clearSessionStorage()
        return Promise.reject()
      }
      // other error code (404, 500, etc): no need to log out
      return Promise.resolve()
    },
    checkAuth: async () => {
      const searchParamsHasCode = new URLSearchParams(window.location.href).has('code')
      // Setup auth state after normal login
      if (searchParamsHasCode) {
        const getTokenResponse = await oktaAuth.token.parseFromUrl()
        const customerRoles = getTokenResponse?.tokens?.accessToken?.claims?.customerType
        const driverRoles = getTokenResponse?.tokens?.accessToken?.claims?.driverType
        if (getTokenResponse?.tokens?.accessToken && getTokenResponse?.tokens?.idToken && getTokenResponse.tokens.accessToken.scopes) {
          const scopes = [
            ...getTokenResponse.tokens.accessToken.scopes,
            ...getScopes(customerRoles, driverRoles).filter(a => a.indexOf('test') === -1)
          ]
          const scopedResponse = await oktaAuth.token.getWithoutPrompt({ scopes })
          oktaAuth.tokenManager.setTokens(scopedResponse.tokens)
          localStorage.setItem('accessToken', JSON.stringify(scopedResponse.tokens.accessToken|| ''))
          localStorage.setItem('idToken', JSON.stringify(scopedResponse.tokens.idToken|| ''))
          sessionStorage.setItem('token', scopedResponse.tokens?.accessToken?.accessToken || '')
        }
        window.location.assign(REDIRECT_URI)
      }
      let tokens = await oktaAuth.tokenManager.getTokens()
      // Attempt to restore tokens from local storage
      if (tokens && Object.keys(tokens).length === 0) {
        try {
          const storedAccessToken = JSON.parse(localStorage.getItem('accessToken') || '')
          const storedIdToken = JSON.parse(localStorage.getItem('idToken') || '')
          if (storedAccessToken && storedIdToken) {
            const [ accessToken, idToken ] = await Promise.all([
              oktaAuth.token.renew(storedAccessToken),
              oktaAuth.token.renew(storedIdToken)
            ])
            if (accessToken && idToken) {
              oktaAuth.tokenManager.add('accessToken', accessToken)
              oktaAuth.tokenManager.add('idToken', idToken) 
              sessionStorage.setItem('token', (<AccessToken>accessToken).accessToken || '')
              history.goBack()
              tokens = await oktaAuth.tokenManager.getTokens()
            }
          }
        } catch { 
          // Couldn't refresh the tokens, clear everything to start clean
          clearSessionStorage()
        }
      }
      if (!tokens || !tokens.accessToken) {
        throw new Error('Unauthorized')
      }
    },
    logout: (args: any) => {
      if (args?.source === 'button') {
        clearSessionStorage()
      }
      return Promise.resolve()
    },
    getIdentity: async () => { 
      const token = await oktaAuth.tokenManager.getTokens()
      const id = token.idToken?.claims.idp
      const fullName = token.idToken?.claims.name
      const avatar = ''
      const email = token.idToken?.claims.email
      const driverRoles = token.accessToken?.claims?.driverType
      return {id, fullName, avatar, email, driverRoles}
    },
    getPermissions: async () => {
      const tokens = await oktaAuth.tokenManager.getTokens()
      if (!tokens || !tokens.accessToken) {
        throw new Error('Unauthorized')
      }
      return tokens.accessToken.scopes
    }
  }
}
